import { extendTheme, Alert, AlertIcon, AlertTitle, AlertDescription, CloseButton, Box } from '@chakra-ui/react'
import type { UseToastOptions, ToastProviderProps, StyleFunctionProps, SystemStyleObject, Theme } from '@chakra-ui/react'
import { createElement } from 'react'
import { glassCardProps } from '@/styles/glassCard'

/**
 * Centralized Chakra UI theme with custom color palette.
 * All colors are aligned with CSS variables in variables.css.
 */

// paire-app#96 round 4 (DE-2 audit finding) — Button/IconButton pressed-state
// fix. A custom `bg`/`background`/`bgColor`/`backgroundColor`/`bgGradient`
// with no `_active` used to fall through to Chakra's stock variant `_active`
// background (e.g. purple.500 -> gray.300 while pressed). This wraps the
// resolved `solid`/`ghost`/`outline` variant functions so a custom
// background's pressed state only dims (`filter: brightness(0.92)`)
// instead. Not a plain `components.Button` override object: `extendTheme`
// deep-merges same-key variant *function* results, so it can't delete
// `_active.bg` by omission — only post-processing the resolved functions
// can. An instance's own `_active` prop still wins regardless (Chakra
// layers instance style props after the theme's). Verified against the
// installed `@chakra-ui/react@2.10.9` source (`extend-theme.mjs`,
// `use-style-config.mjs`, `.pnpm/@chakra-ui+theme@.../components/button.mjs`)
// — full trace: `paire-wiki/Architecture/Appearance Mode & Card Token
// System.md` §"Button pressed state".
export const BG_STYLE_PROPS = ['bg', 'background', 'bgColor', 'backgroundColor', 'bgGradient'] as const

function hasCustomBg(props: StyleFunctionProps): boolean {
  return BG_STYLE_PROPS.some((key) => props[key] != null)
}

function withHeldActiveBg(
  variantFn: (props: StyleFunctionProps) => SystemStyleObject,
): (props: StyleFunctionProps) => SystemStyleObject {
  return (props: StyleFunctionProps): SystemStyleObject => {
    const styles = variantFn(props)
    if (!hasCustomBg(props)) return styles

    const { _active, ...rest } = styles as { _active?: SystemStyleObject; [key: string]: unknown }
    const activeRest = { ...(_active ?? {}) } as Record<string, unknown>
    // Real omission (post-processing the resolved function), not a merge
    // that could leave the stock value in place — see the doc comment above.
    for (const key of BG_STYLE_PROPS) delete activeRest[key]

    return {
      ...rest,
      _active: {
        ...activeRest,
        filter: 'brightness(0.92)',
      },
    }
  }
}

const theme = extendTheme(
  {
    // D45a — appearance mode is owned by our own AppearanceProvider/useAppearance
    // (see src/lib/appearance), never by Chakra's own OS listener. This must
    // stay false permanently: useSystemColorMode:true would flip an explicit
    // Light/Dark choice on every OS change (d45_mode_wiring_decision.md §1.3).
    config: {
      initialColorMode: 'system',
      useSystemColorMode: false,
    },
    // Locked magazine type scale (FR-T12): Caption 12 = xs, Body 14 = sm, Headline 20 = xl,
    // Input step 16 = md, Large-title 18 = lg, Desktop-top 30 = 3xl are all direct reuses
    // of Chakra's default scale and need no override. Only the steps with zero production
    // precedent are declared here. Desktop 23/26 reuse `display`/`stat` (same pixel values).
    // `2xs` is redefined to equal `xs` (12px) so no code path can produce sub-floor 10px text.
    fontSizes: {
      '2xs': '0.75rem', // 12px — was 10px; floored to the Caption(12) value (FR-T12 2xs disposition)
      small: '0.8125rem', // 13px — Small(13), no prior production value
      title: '0.9375rem', // 15px — Title(15), no prior production value
      display: '1.4375rem', // 23px — Display(23); also covers the Desktop-23 step
      stat: '1.625rem', // 26px — Stat(26); also covers the Desktop-26 step
    },
    colors: {
      purple: {
        50: '#F5F0FF',
        100: '#E9DBFF',
        200: '#D4BFFF',
        300: '#B794F6',
        400: '#9F7AEA',
        500: '#7C3AED', // Primary purple
        600: '#6B2BD9',
        700: '#5A21B6',
        800: '#4A1D96',
        900: '#3B1578',
      },
      gray: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827',
      },
      green: {
        50: '#ECFDF5',
        100: '#D1FAE5',
        200: '#A7F3D0',
        300: '#6EE7B7',
        400: '#34D399',
        500: '#10B981',
        600: '#059669',
        700: '#047857',
        800: '#065F46',
        900: '#064E3B',
      },
      orange: {
        50: '#FFF7ED',
        100: '#FFEDD5',
        200: '#FED7AA',
        300: '#FDBA74',
        400: '#FB923C',
        500: '#FF6B35', // Secondary orange
        600: '#EA580C',
        700: '#C2410C',
        800: '#9A3412',
        900: '#7C2D12',
      },
      red: {
        50: '#FEF2F2',
        100: '#FEE2E2',
        200: '#FECACA',
        300: '#FCA5A5',
        400: '#F87171',
        500: '#EF4444',
        600: '#DC2626',
        700: '#B91C1C',
        800: '#991B1B',
        900: '#7F1D1D',
      },
      teal: {
        50: '#F0FDFA',
        100: '#CCFBF1',
        200: '#99F6E4',
        300: '#5EEAD4',
        400: '#2DD4BF',
        500: '#2A9D8F', // Tertiary teal
        600: '#0D9488',
        700: '#0F766E',
        800: '#115E59',
        900: '#134E4A',
      },
      blue: {
        50: '#EFF6FF',
        100: '#DBEAFE',
        200: '#BFDBFE',
        300: '#93C5FD',
        400: '#60A5FA',
        500: '#3B82F6',
        600: '#2563EB',
        700: '#1D4ED8',
        800: '#1E40AF',
        900: '#1E3A8A',
      },
      yellow: {
        50: '#FEFCE8',
        100: '#FEF9C3',
        200: '#FEF08A',
        300: '#FDE047',
        400: '#FACC15',
        500: '#EAB308',
        600: '#CA8A04',
        700: '#A16207',
        800: '#854D0E',
        900: '#713F12',
      },
      pink: {
        50: '#FDF2F8',
        100: '#FCE7F3',
        200: '#FBCFE8',
        300: '#F9A8D4',
        400: '#F472B6',
        500: '#EC4899',
        600: '#DB2777',
        700: '#BE185D',
        800: '#9D174D',
        900: '#831843',
      },
      cyan: {
        50: '#ECFEFF',
        100: '#CFFAFE',
        200: '#A5F3FC',
        300: '#67E8F9',
        400: '#22D3EE',
        500: '#06B6D4',
        600: '#0891B2',
        700: '#0E7490',
        800: '#155E75',
        900: '#164E63',
      },
      // Custom color palette for pins, icons, and UI accents
      palette: {
        skyBlue: '#67cefe',
        indigo: '#5f4cf6',
        forestGreen: '#1c5d3a',
        violet: '#766cff',
        limeGreen: '#30c412',
        brightRed: '#ff3131',
        emerald: '#379d4b',
        oceanBlue: '#147db8',
        magenta: '#cb1ed4',
        hotPink: '#ff44ec',
        slateIndigo: '#5f58ee',
        powderBlue: '#76c6ee',
        orchid: '#e879ff',
        periwinkle: '#6a73ff',
        sunflower: '#fad841',
        kellyGreen: '#02843a',
        mintGreen: '#10ea77',
        cottonCandy: '#fcafe8',
        royalBlue: '#0859ff',
        tangerine: '#ff974b',
        lime: '#c2ff74',
        lemon: '#fffd57',
        apricot: '#ffa03e',
        aquamarine: '#3bd5bd',
        fuchsia: '#fd6ed8',
        seafoam: '#21e6b7',
        turquoise: '#5affeb',
        peach: '#ffba8f',
        electricCyan: '#1cc7ef',
      },
    },
  },
  // Function-extension form (second `extendTheme` argument): receives the
  // already-merged theme from the object override above and its return
  // value becomes the theme outright, with no further deep-merge — verified
  // against `extend-theme.mjs`'s `createExtendTheme`/`pipe`. This is what
  // lets the Button pressed-state fix (`withHeldActiveBg`, above) delete a
  // key on the already-resolved stock variant functions instead of fighting
  // `extendTheme`'s own auto-composition of same-key variant functions.
  (prevTheme: Theme) => {
    const prevVariants = prevTheme.components?.Button?.variants as
      | Record<string, (props: StyleFunctionProps) => SystemStyleObject>
      | undefined
    if (!prevVariants) return prevTheme

    const variants = { ...prevVariants }
    for (const key of ['solid', 'ghost', 'outline'] as const) {
      const stockVariant = prevVariants[key]
      if (typeof stockVariant === 'function') {
        variants[key] = withHeldActiveBg(stockVariant)
      }
    }
    // `link`/`unstyled` (and any other variant) are left exactly as Chakra
    // shipped them — `link`'s `_active` is a text-color swap, not a
    // background swap, and `unstyled` has no theme-driven bg to conflict
    // with in the first place.

    return {
      ...prevTheme,
      components: {
        ...prevTheme.components,
        Button: {
          ...prevTheme.components.Button,
          variants,
        },
      },
    }
  },
)

export default theme

/**
 * D45b Phase 6 (FR-N9) — toast-render-path glass override, the 6th glass
 * roster surface ("toasts/confirmations"). This is intentionally NOT a
 * `components.Alert` theme-recipe override: `<Alert>` is also used directly
 * as static in-page content at 7 confirmed sites across 4 files
 * (`ReportMenuItemModal.tsx`, `MenuItemsStep.tsx`, `SubscriptionSettings.tsx`,
 * `QuizTaking.tsx` — none of them roster members), and a theme-level `Alert`
 * recipe override would silently glass all 7 of those too.
 *
 * Instead this hooks Chakra's toast-specific render path, verified via
 * Context7 against the installed `@chakra-ui/react@2.10.9` and confirmed by
 * reading the package's own source (`create-toast-fn.mjs`,
 * `toast.provider.d.ts`): `ChakraProvider`'s `toastOptions.defaultOptions`
 * (typed `UseToastOptions`) is merged UNDER each call's own options by
 * `createToastFn`'s `normalizeToastOptions` (`{ ...defaultOptions,
 * ...options }`), so a `render` function set here fires for every
 * `useToast()` call site that doesn't pass its own `render` — every
 * existing call site (~70+ across the app) picks this up with zero
 * call-site changes. It never fires for a directly-rendered `<Alert>`,
 * since that mechanism is entirely separate from the toast manager.
 *
 * Status color signal is preserved without relying on the (now-translucent)
 * background: `AlertIcon`'s glyph shape already differs per status
 * (checkmark/warning triangle/×/i — a built-in Chakra behavior, not
 * something this file adds), and its color is set to a status accent here
 * so both shape AND color distinguish variants — satisfying "no visual
 * state relies on color alone" without inventing a second mechanism.
 */
const TOAST_STATUS_ACCENT: Record<string, string> = {
  success: 'green.400',
  error: 'red.400',
  warning: 'orange.400',
  info: 'blue.400',
  loading: 'blue.400',
}

type ToastRenderProps = UseToastOptions & { id?: string | number; onClose: () => void }

function renderGlassToast(props: ToastRenderProps) {
  const { id, onClose, status = 'info', title, description, isClosable, colorScheme } = props
  const accent = TOAST_STATUS_ACCENT[status] ?? TOAST_STATUS_ACCENT.info
  const ids = id
    ? { root: `toast-${id}`, title: `toast-${id}-title`, description: `toast-${id}-description` }
    : undefined

  return createElement(
    Alert,
    {
      addRole: false,
      status,
      id: ids?.root,
      alignItems: 'start',
      boxShadow: 'lg',
      paddingEnd: 8,
      textAlign: 'start',
      width: 'auto',
      colorScheme,
      color: 'white',
      // bg intentionally stays var(--color-overlay-60) (a dark scrim), not
      // glassCardProps.bg — toasts can render over arbitrary page content
      // (unlike the roster's fixed-chrome surfaces, which always sit over
      // a consistent background) and need real darkening so `color: 'white'`
      // stays legible in light mode. Matches AppHeader.tsx / FilterBar.tsx /
      // InfoPopover.tsx / MainNavigationTabBar.tsx's carve-out (D45b Phase
      // 6, FR-N9; round-2 review fix N3) — only the blur/saturate/brightness
      // recipe (`glassCardProps.backdropFilter`) is shared, not
      // `glassCardProps.bg`.
      bg: 'var(--color-overlay-60)',
      backdropFilter: glassCardProps.backdropFilter,
      borderRadius: glassCardProps.borderRadius,
      position: glassCardProps.position,
      overflow: glassCardProps.overflow,
      sx: {
        ...glassCardProps.sx,
        WebkitBackdropFilter: glassCardProps.backdropFilter,
        '@supports not (backdrop-filter: blur(1px))': {
          bg: 'blackAlpha.900',
          backdropFilter: 'none',
        },
      },
    },
    createElement(AlertIcon, { key: 'icon', color: accent }),
    createElement(
      Box,
      { key: 'body', flex: '1', maxWidth: '100%' },
      title && createElement(AlertTitle, { key: 'title', id: ids?.title }, title),
      description &&
        createElement(AlertDescription, { key: 'description', id: ids?.description, display: 'block' }, description),
    ),
    isClosable &&
      createElement(CloseButton, {
        key: 'close',
        size: 'sm',
        onClick: onClose,
        position: 'absolute',
        insetEnd: 1,
        top: 1,
      }),
  )
}

/**
 * Passed to `<ChakraProvider toastOptions={toastOptions}>` in `main.tsx`.
 * Lives here (not inline in `main.tsx`) so the glass roster's toast
 * treatment is co-located with the rest of the glass roster's shared
 * values (`glassCardProps`) rather than split across two files.
 */
export const toastOptions: ToastProviderProps = {
  defaultOptions: {
    render: renderGlassToast,
  },
}
